import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { PnlCalculationResult } from '../../hooks/usePnlCalculation';
import { formatVolume } from '../../utils/formatters';

interface FilledVolumeTooltipProps {
  pnl: PnlCalculationResult;
  theme: any;
  formatUsdt: (val: number | null | undefined) => string;
  formatProfit: (val: number | null | undefined) => string;
  symbol: string;
}

export const FilledVolumeTooltip: React.FC<FilledVolumeTooltipProps> = ({
  pnl,
  theme,
  formatUsdt,
  formatProfit,
  symbol
}) => {
  const spotRealized = pnl.profit.spotRealizedPnl ?? 0;
  const futuresRealized = pnl.profit.futuresRealizedPnl ?? 0;
  const totalRealized = spotRealized + futuresRealized;

  // Calculate distinct values for tooltip
  const spotCostFilled = (pnl.spot.avgPriceUsdt ?? 0) * pnl.balance.spotAsked;
  // For futures (Short): Realized = (Entry - Exit) * Vol => ExitVal = EntryVal - Realized
  const futuresEntryFilled = (pnl.futures.avgPrice ?? 0) * pnl.balance.futuresClosed;

  return (
    <Box sx={{ p: 1.5, minWidth: 280 }}>
      {/* Realized PnL Header */}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'common.white' }}>
        Realized PnL (Filled)
      </Typography>
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Net Realized Profit */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.9)', pl: 1 }}>Net Realized:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: totalRealized >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
            {formatProfit(totalRealized)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Spot Breakdown */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.spot, display: 'block', mb: 0.5 }}>
          Spot Sold ({formatVolume(pnl.balance.spotAsked, 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Cost Basis:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(spotCostFilled)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Realized PnL:</Typography>
            <Typography variant="caption" sx={{ color: spotRealized >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
              {formatProfit(spotRealized)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Futures Breakdown */}
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.futures, display: 'block', mb: 0.5 }}>
          Futures Closed ({formatVolume(pnl.balance.futuresClosed, 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Entry Value:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(futuresEntryFilled)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Realized PnL:</Typography>
            <Typography variant="caption" sx={{ color: futuresRealized >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
              {formatProfit(futuresRealized)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
